'use client';

import { c as _c } from "react/compiler-runtime";
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { usersApi } from '../api/endpoints';
import type { ApiUser, UsersQuery, UsersResponse } from '../api/types';
import { useCustomQuery } from '../query/useCustomQuery';
import { notify, toErrorMessage } from '../utils/notify';

/** Query key factory for the admin users list. */
export const usersKeys = {
  all: ['users'] as const,
  lists: () => [...usersKeys.all, 'list'] as const,
  list: (query?: UsersQuery) => [...usersKeys.lists(), query] as const
};
export interface UpdateUserData {
  name?: string;
  email?: string;
  image?: string | null;
  tags?: string[];
  approved?: boolean;
  data?: Record<string, unknown>;
}
export interface CreateUserData {
  name: string;
  email: string;
  image?: string | null;
  approved?: boolean;
  data?: Record<string, unknown>;
}
const emptyPagination: UsersResponse['pagination'] = {
  limit: 20,
  nextCursor: null,
  prevCursor: null,
  hasMore: false
};

/**
 * Fetch users with filtering and cursor pagination. Admin-only endpoint.
 *
 * @example
 * ```tsx
 * const { users, pagination, isLoading } = useUsers({ approved: false, limit: 30 });
 * ```
 */
export function useUsers(query, t0) {
  const $ = _c(17);
  const enabled = t0 === undefined ? true : t0;
  let t1;
  if ($[0] !== query) {
    t1 = usersKeys.list(query);
    $[0] = query;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== query) {
    t2 = () => usersApi.getUsers(query);
    $[2] = query;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== enabled || $[5] !== t1 || $[6] !== t2) {
    t3 = {
      queryKey: t1,
      queryFn: t2,
      staleTime: 30000,
      enabled
    };
    $[4] = enabled;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const result = useCustomQuery(t3);
  let t4;
  if ($[8] !== result.data?.users) {
    t4 = result.data?.users ?? [];
    $[8] = result.data?.users;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const t5 = result.data?.pagination ?? emptyPagination;
  const t6 = result.error as Error | null;
  let t7;
  if ($[10] !== result.isFetching || $[11] !== result.isLoading || $[12] !== result.refetch || $[13] !== t4 || $[14] !== t5 || $[15] !== t6) {
    t7 = {
      users: t4,
      pagination: t5,
      isLoading: result.isLoading,
      isFetching: result.isFetching,
      error: t6,
      refetch: result.refetch
    };
    $[10] = result.isFetching;
    $[11] = result.isLoading;
    $[12] = result.refetch;
    $[13] = t4;
    $[14] = t5;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  return t7;
}

/**
 * Create / update / delete users with optimistic cache updates and rollback.
 *
 * Feedback is routed through the shared notifier, so an app that hasn't called
 * `configureNotifier` still gets console output — surface `error` state in the UI too.
 */
export function useUserManagement() {
  const $ = _c(43);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== queryClient) {
    t0 = updater => {
      queryClient.setQueriesData({
        queryKey: usersKeys.lists()
      }, old => {
        const previous = old as UsersResponse | undefined;
        if (!previous?.users) {
          return old;
        }
        return {
          ...previous,
          users: updater(previous.users)
        };
      });
    };
    $[0] = queryClient;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const patchListCaches = t0;
  let t1;
  if ($[2] !== queryClient) {
    t1 = async () => {
      await queryClient.cancelQueries({
        queryKey: usersKeys.lists()
      });
      return queryClient.getQueriesData({
        queryKey: usersKeys.lists()
      });
    };
    $[2] = queryClient;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const snapshotLists = t1;
  let t2;
  if ($[4] !== queryClient) {
    t2 = previousQueries => {
      previousQueries?.forEach(t3 => {
        const [queryKey, data] = t3;
        queryClient.setQueryData(queryKey, data);
      });
    };
    $[4] = queryClient;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const rollback = t2;
  let t3;
  if ($[6] !== patchListCaches || $[7] !== snapshotLists) {
    t3 = async t4 => {
      const {
        userId: userId_0,
        data: data_1
      } = t4;
      const previousQueries_0 = await snapshotLists();
      patchListCaches(users => users.map(user => user.id === userId_0 ? {
        ...user,
        ...data_1
      } : user));
      return {
        previousQueries: previousQueries_0
      };
    };
    $[6] = patchListCaches;
    $[7] = snapshotLists;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== rollback) {
    t4 = (error, _variables, context) => {
      rollback(context?.previousQueries);
      notify.error(toErrorMessage(error, "Failed to update user"));
    };
    $[9] = rollback;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== patchListCaches) {
    t5 = updatedUser => {
      patchListCaches(users_0 => users_0.map(user_0 => user_0.id === updatedUser.id ? updatedUser : user_0));
      notify.success("User updated");
    };
    $[11] = patchListCaches;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== t3 || $[14] !== t4 || $[15] !== t5) {
    t6 = {
      mutationFn: _temp,
      onMutate: t3,
      onError: t4,
      onSuccess: t5
    };
    $[13] = t3;
    $[14] = t4;
    $[15] = t5;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  const updateUserMutation = useMutation(t6);
  let t7;
  if ($[17] !== patchListCaches || $[18] !== snapshotLists) {
    t7 = async userId_2 => {
      const previousQueries_1 = await snapshotLists();
      patchListCaches(users_1 => users_1.filter(user_1 => user_1.id !== userId_2));
      return {
        previousQueries: previousQueries_1
      };
    };
    $[17] = patchListCaches;
    $[18] = snapshotLists;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== rollback) {
    t8 = (error_0, _variables_0, context_0) => {
      rollback(context_0?.previousQueries);
      notify.error(toErrorMessage(error_0, "Failed to delete user"));
    };
    $[20] = rollback;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  let t9;
  if ($[22] !== t7 || $[23] !== t8) {
    t9 = {
      mutationFn: _temp2,
      onMutate: t7,
      onError: t8,
      onSuccess: _temp3
    };
    $[22] = t7;
    $[23] = t8;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  const deleteUserMutation = useMutation(t9);
  let t10;
  if ($[25] !== patchListCaches) {
    t10 = {
      mutationFn: _temp4,
      onSuccess: newUser => {
        patchListCaches(users_2 => [newUser, ...users_2]);
        notify.success("User created");
      },
      onError: _temp5
    };
    $[25] = patchListCaches;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  const createUserMutation = useMutation(t10);
  let t11;
  if ($[27] !== updateUserMutation) {
    t11 = (userId_3, data_3) => updateUserMutation.mutateAsync({
      userId: userId_3,
      data: data_3
    });
    $[27] = updateUserMutation;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  let t12;
  if ($[29] !== deleteUserMutation) {
    t12 = userId_4 => deleteUserMutation.mutateAsync(userId_4);
    $[29] = deleteUserMutation;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  let t13;
  if ($[31] !== createUserMutation) {
    t13 = data_4 => createUserMutation.mutateAsync(data_4);
    $[31] = createUserMutation;
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  let t14;
  if ($[33] !== updateUserMutation) {
    t14 = (userId_5, approved) => updateUserMutation.mutateAsync({
      userId: userId_5,
      data: {
        approved
      }
    });
    $[33] = updateUserMutation;
    $[34] = t14;
  } else {
    t14 = $[34];
  }
  let t15;
  if ($[35] !== createUserMutation.isPending || $[36] !== deleteUserMutation.isPending || $[37] !== t11 || $[38] !== t12 || $[39] !== t13 || $[40] !== t14 || $[41] !== updateUserMutation.isPending) {
    t15 = {
      updateUser: t11,
      deleteUser: t12,
      createUser: t13,
      setUserApproval: t14,
      isUpdating: updateUserMutation.isPending,
      isDeleting: deleteUserMutation.isPending,
      isCreating: createUserMutation.isPending
    };
    $[35] = createUserMutation.isPending;
    $[36] = deleteUserMutation.isPending;
    $[37] = t11;
    $[38] = t12;
    $[39] = t13;
    $[40] = t14;
    $[41] = updateUserMutation.isPending;
    $[42] = t15;
  } else {
    t15 = $[42];
  }
  return t15;
}
function _temp5(error_1) {
  notify.error(toErrorMessage(error_1, "Failed to create user"));
}
function _temp4(data_2) {
  return usersApi.createUser(data_2);
}
function _temp3() {
  notify.success("User deleted");
}
function _temp2(userId_1) {
  return usersApi.deleteUser(userId_1);
}
function _temp(t0) {
  const {
    userId,
    data: data_0
  } = t0;
  return usersApi.updateUser(userId, data_0);
}